<template>
    <div class="performance-item">
        <div class="list-group-item performance-item__header px-2" v-b-toggle="collapseId" tabindex="-1">
            <div class="row mx-n2">
                <div class="col-auto px-2">
                    <div class="performance-item__label">
                        <div class="performance-item__month text-primary text-uppercase">
                            {{ monthLabel }}
                        </div>
                        <div class="performance-item__day text-primary agency-title-font">
                            {{ dayLabel }}
                        </div>
                        <div class="performance-item__hour text-primary">
                            {{ hourLabel }}
                        </div>
                    </div>
                </div>

                <div class="col-auto px-2 d-none d-md-block" style="width: 100px">
                    <div class="responsive-ratio responsive-ratio-1/1">
                        <template v-if="thumbnail">
                            <img class="responsive-ratio-item" :src="thumbnail.thumbnail" :alt="imageAlt(thumbnail)" style="object-fit: cover">
                        </template>
                        <template v-else>
                            <div class="responsive-ratio-item bg-lightsalmon" style="opacity: .5"></div>
                        </template>
                    </div>
                </div>

                <div class="col px-2" style="min-width: 0">
                    <div class="row">
                        <div class="col-sm-6">
                            <h3 class="performance-item__title font-weight-bold">{{ showName }}</h3>
                            <template v-if="showCompany">
                                <h4 class="performance-item__subtitle text-muted">{{ companyName }}</h4>
                            </template>
                        </div>
                        <div class="col-sm-6 d-none d-sm-block">
                            <div class="performance-item__room">
                                <template v-if="venueName">
                                    <a :href="venueUrl" @click.stop>
                                        <strong>{{ venueName }}</strong>
                                    </a>
                                    –
                                </template>
                                <strong>{{ roomName }}</strong>
                                <br>
                                {{ roomCity }} ({{ roomDepartment }})
                            </div>
                            <div class="performance-item__audience text-muted mt-1">{{ audience }}</div>
                            <div class="performance-item__artistic-fields text-muted mt-1 mw-100 text-truncate text-lowercase" :title="artisticFieldsLabel">
                                {{ artisticFieldsLabel }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-auto align-self-center px-2">
                    <button class="btn performance-item__expand-button" @click.stop @keydown.stop v-b-toggle="collapseId">
                        <svg class="sprite-inline">
                            <use href="#sprite-plus" xlink:href="#sprite-plus"></use>
                        </svg>
                        <span class="minus" style="width: 1em" v-cloak>
                            —
                        </span>
                    </button>
                </div>
            </div>
        </div>
        <b-collapse :id="collapseId" @show="handleCollapseShow" ref="collapse">
            <div class="pb-4">
                <div class="list-group-item border-0 bg-lightsalmon px-md-4 pb-md-3">
                    <div class="row mx-n2">
                        <template v-if="visuals.length > 0">
                            <div class="col-sm-5 px-2 order-sm-1 mb-3 mb-sm-0">
                                <div class="d-flex justify-content-start justify-content-sm-end align-items-start">
                                    <div class="position-relative">
                                        <img class="img-fluid" :src="visuals[0].thumbnail" :alt="imageAlt(visuals[0])">
                                        <button class="btn text-primary bg-white" style="position: absolute; bottom: .5rem; right: .5rem" @click="handleZoomClicked">
                                            <svg class="fill-current" width="25" height="25">
                                                <use xlink:href="#sprite-zoom" href="#sprite-zoom"></use>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-if="loading">
                            <div class="col px-2">
                                <div class="spinner-grow spinner-grow-sm text-white"></div>
                            </div>
                        </template>
                        <template v-else>
                            <div class="col-sm-7 px-2">
                                <div class="mb-2">
                                    <template v-if="showCompany">
                                        <h4 style="font-size: 1rem">
                                            <a :href="companyUrl" @click.stop>{{ companyName }}</a>
                                        </h4>
                                    </template>
                                    <div class="performance-item__room d-flex flex-wrap d-sm-none">
                                        <template v-if="venueName">
                                            <span>
                                                <a :href="venueUrl" @click.stop>
                                                    <strong>{{ venueName }}</strong>
                                                </a>
                                                –
                                            </span>
                                        </template>
                                        <strong>{{ roomName }}</strong>
                                        <span>, {{ roomCity }} ({{ roomDepartment }})</span>
                                    </div>
                                </div>
                                <div v-html="showDescription"></div>
                                <div class="mt-2">
                                    <div class="small d-sm-none" style="opacity: .675">{{ audience }}</div>
                                    <div class="small text-lowercase" style="opacity: .675">{{ artisticFieldsLabel }}</div>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </b-collapse>
        <b-modal
            data-legacy
            v-model="showModal"
            size="xl"
            header-class="modal-header--absolute"
            body-class="p-0 pt-md-4"
            hide-footer
            centered
        >
            <template v-slot:modal-header-close>
                <svg class="d-block fill-current text-primary" width="20" height="20">
                    <use xlink:href="#sprite-close"></use>
                </svg>
            </template>
            <b-tabs
                align="center"
                nav-class="p-2 p-sm-3 bg-white"
                content-class="p-2 p-sm-3"
                :nav-wrapper-class="!hasNav ? 'd-none' : ''"
                active-nav-item-class="border-primary"
                no-nav-styles
                lazy
                end
            >
                <template v-for="visual in visuals">
                    <b-tab title-link-class="px-1 mx-1 border">
                        <template v-slot:title>
                            <div style="width: 70px">
                                <b-aspect class="b-aspect--center b-aspect--fixed" :aspect="1">
                                    <img class="img-fluid mh-100 w-auto" :src="visual.thumbnail" :alt="imageAlt(visual)">
                                </b-aspect>
                            </div>
                        </template>
                        <div class="text-center">
                            <b-aspect class="b-aspect--center overflow-hidden" :aspect="16/9" style="max-height: calc(100vh - 200px)">
                                <img class="img-fluid h-auto mh-100" :src="visual.large" :alt="imageAlt(visual)">
                            </b-aspect>
                        </div>
                    </b-tab>
                </template>
                <template v-if="videoUrl">
                    <b-tab title-link-class="px-1 mx-1 border">
                        <template v-slot:title>
                            <div style="width: 70px">
                                <b-aspect class="b-aspect--center" :aspect="1">
                                    <svg class="sprite-play" width="30" height="30">
                                        <use xlink:href="#sprite-play"></use>
                                    </svg>
                                </b-aspect>
                            </div>
                        </template>
                        <div class="text-center">
                            <b-aspect class="overflow-hidden" :aspect="16/9" style="max-height: calc(100vh - 200px)">
                                <EmbedVideo :url="videoUrl" show :autoplay="false" inner />
                            </b-aspect>
                        </div>
                    </b-tab>
                </template>
            </b-tabs>
        </b-modal>
    </div>
</template>

<script>
    import dayjs from 'dayjs';
    import { getShow } from "./api";
    import { BAspect, BModal, BCollapse, BTabs, BTab, VBToggle } from 'bootstrap-vue';
    import EmbedVideo from "../../EmbedVideo.vue";
    import { updateCollapseHeight } from "../../../util/collapse";

    export default {
        components: {
            BAspect,
            BModal,
            BCollapse,
            BTabs,
            BTab,
            EmbedVideo,
        },
        props: {
            performance: Object,
            audiences: Object,
            showCompany: Boolean,
        },
        data() {
            return {
                show: null,
                showModal: false,
                loading: true,
            }
        },
        computed: {
            hasNav() {
                const items = [...this.visuals, this.videoUrl].filter(item => !!item);
                return items.length > 1;
            },
            dayLabel() {
                return dayjs(this.performance.starts_at).format('D')
            },
            monthLabel() {
                return dayjs(this.performance.starts_at).format('MMM').replace('.', '');
            },
            hourLabel() {
                return dayjs(this.performance.starts_at)
                    .format('H[h] mm');
            },
            showName() {
                return this.performance.show.name;
            },
            showDescription() {
                return this.performance.show.description;
            },
            companyName() {
                return this.performance.show.company.name;
            },
            companyUrl() {
                return this.performance.show.company.url;
            },
            artisticFieldsLabel() {
                return this.performance.show.artisticFields
                    .map(field => field.name)
                    .join(' • ');
            },
            venueName() {
                return this.performance.venueRoom?.venue.title;
            },
            roomName() {
                return this.performance.venueRoom
                    ? this.performance.venueRoom.name
                    : this.performance.room_name;
            },
            roomDepartment() {
                return this.performance.venueRoom
                    ? this.performance.venueRoom.postal_code.substring(0, 2)
                    : this.performance.room_department;
            },
            roomCity() {
                return this.performance.venueRoom
                    ? this.performance.venueRoom.city
                    : this.performance.room_city;
            },
            venueUrl() {
                return this.performance.venueRoom?.venue.url;
            },
            audience() {
                return this.performance.audience
                    ? this.audiences?.[this.performance.audience]
                    : null;
            },
            visuals() {
                return this.show?.visuals ?? [];
            },
            videoUrl() {
                return this.show?.video_url;
            },
            thumbnail() {
                return this.performance.show.thumbnail;
            },
            collapseId() {
                return `performance-${this.performance.id}`;
            },
        },
        methods: {
            handleCollapseShow() {
                if(this.show) {
                    return;
                }
                this.loading = true;
                getShow(this.performance.show.id)
                    .then(show => {
                        this.show = show;
                    })
                    .finally(() => {
                        this.loading = false;
                        updateCollapseHeight(this.$refs.collapse.$el);
                    })
            },
            handleZoomClicked() {
                this.showModal = true;
            },
            handleRoomClicked(e) {
                if(e.target.getAttribute('href')) {
                    e.stopPropagation();
                } else {
                    e.preventDefault();
                }
            },
            imageAlt(visual) {
                return visual.legend || this.showName;
            },
        },
        directives: {
            'v-b-toggle': VBToggle,
        }
    }
</script>
